import { useState, useEffect, useMemo } from 'react';
import { useOrgNavigate } from '@/hooks/useOrgNavigate';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from '@/hooks/useTranslation';
import { FullscreenFormDialog } from '@/components/ui/fullscreen-form-dialog';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useCreateTimesheet, useUpdateTimesheet, Timesheet, type VisibilityLevel } from '@/hooks/useTimesheets';
import { VisibilitySelect } from '@/components/ui/visibility-select';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCurrentUserOrgRole } from '@/hooks/useCurrentUserOrgRole';
import { usePersonIndex } from '@/hooks/usePeople';
import { useProjectIndex } from '@/hooks/useProjectIndex';
import { useBusinessIndex } from '@/hooks/useBusinesses';

/** Visibility levels a non-admin may choose (the DB refuses 'admins' for them). */
const NON_ADMIN_VISIBILITY_LEVELS = ['members', 'private'] as const;

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  visibility: z.enum(['members', 'admins', 'private']).default('members'),
  project_id: z.string().optional(),
  business_id: z.string().optional(),
  person_id: z.string().optional(),
  columns: z.object({
    is_estimated: z.boolean(),
    is_billable: z.boolean(),
    project_id: z.boolean(),
    person_id: z.boolean(),
    business_id: z.boolean(),
    task: z.boolean(),
    entry_date: z.boolean(),
    start_time: z.boolean(),
    end_time: z.boolean(),
    note: z.boolean(),
  }),
});

type FormValues = z.infer<typeof formSchema>;

// Column label keys mapped to translation keys
const columnLabelKeys: Record<string, string> = {
  is_estimated: 'columns.estimated',
  is_billable: 'columns.billable',
  project_id: 'columns.project',
  person_id: 'columns.person',
  business_id: 'columns.business',
  task: 'columns.task',
  entry_date: 'columns.date',
  start_time: 'columns.startTime',
  end_time: 'columns.endTime',
  note: 'columns.notes',
};

// Default column configuration (DRY)
const DEFAULT_COLUMN_CONFIG = {
  is_estimated: true,
  is_billable: true,
  project_id: true,
  person_id: true,
  business_id: true,
  task: true,
  entry_date: true,
  start_time: true,
  end_time: true,
  note: true,
} as const;

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

interface CreateTimesheetDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  // Edit mode - if timesheet provided, we're editing
  readonly timesheet?: Timesheet;
  readonly onComplete?: () => void;
}

export function CreateTimesheetDialog({
  open,
  onOpenChange,
  timesheet,
  onComplete,
}: CreateTimesheetDialogProps) {
  const isEditMode = !!timesheet;
  const orgNavigate = useOrgNavigate();
  const createTimesheet = useCreateTimesheet();
  const updateTimesheet = useUpdateTimesheet();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedOrganization } = useOrganization();
  // Only org admins may choose "Admins only" — the database refuses it for
  // anyone else (timesheets_insert / timesheets_update WITH CHECK).
  const { isAdmin } = useCurrentUserOrgRole();
  const { t } = useTranslation('timesheets');

  // Lookup data — the shared org indexes (complete, API-paged), the same
  // cache entries the Projects / Businesses / People grids read. Hosts mount
  // this dialog only while it is open, so the reads fire only then.
  const { data: projectRows = [] } = useProjectIndex(open ? selectedOrganization?.id : undefined);
  const { data: businessRows = [] } = useBusinessIndex();
  const projects = useMemo(() => [...projectRows].sort(byName), [projectRows]);
  const businesses = useMemo(() => [...businessRows].sort(byName), [businessRows]);
  const { data: people = [] } = usePersonIndex(open ? selectedOrganization?.id : undefined, {
    sortByName: true,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      visibility: 'members' as VisibilityLevel,
      project_id: '',
      business_id: '',
      person_id: '',
      columns: { ...DEFAULT_COLUMN_CONFIG },
    },
  });

  // Reset form when dialog opens or timesheet changes
  useEffect(() => {
    if (open && timesheet) {
      form.reset({
        name: timesheet.name,
        description: timesheet.description || '',
        visibility: timesheet.visibility || 'members',
        project_id: timesheet.project_id || '',
        business_id: timesheet.business_id || '',
        person_id: timesheet.person_id || '',
        columns: {
          is_estimated: timesheet.column_config?.is_estimated ?? DEFAULT_COLUMN_CONFIG.is_estimated,
          is_billable: timesheet.column_config?.is_billable ?? DEFAULT_COLUMN_CONFIG.is_billable,
          project_id: timesheet.column_config?.project_id ?? DEFAULT_COLUMN_CONFIG.project_id,
          person_id: timesheet.column_config?.person_id ?? DEFAULT_COLUMN_CONFIG.person_id,
          business_id: timesheet.column_config?.business_id ?? DEFAULT_COLUMN_CONFIG.business_id,
          task: timesheet.column_config?.task ?? DEFAULT_COLUMN_CONFIG.task,
          entry_date: timesheet.column_config?.entry_date ?? DEFAULT_COLUMN_CONFIG.entry_date,
          start_time: timesheet.column_config?.start_time ?? DEFAULT_COLUMN_CONFIG.start_time,
          end_time: timesheet.column_config?.end_time ?? DEFAULT_COLUMN_CONFIG.end_time,
          note: timesheet.column_config?.note ?? DEFAULT_COLUMN_CONFIG.note,
        },
      });
    } else if (open && !timesheet) {
      form.reset({
        name: '',
        description: '',
        visibility: 'members',
        project_id: '',
        business_id: '',
        person_id: '',
        columns: { ...DEFAULT_COLUMN_CONFIG },
      });
    }
  }, [open, timesheet, form]);

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        // Update existing timesheet
        await updateTimesheet.mutateAsync({
          id: timesheet.id,
          name: values.name,
          description: values.description,
          visibility: values.visibility as VisibilityLevel,
          column_config: values.columns,
          project_id: values.project_id || null,
          business_id: values.business_id || null,
          person_id: values.person_id || null,
        });
        onComplete?.();
        onOpenChange(false);
      } else {
        // Create new timesheet
        const newTimesheet = await createTimesheet.mutateAsync({
          name: values.name,
          description: values.description,
          visibility: values.visibility as VisibilityLevel,
          column_config: values.columns,
          project_id: values.project_id || null,
          business_id: values.business_id || null,
          person_id: values.person_id || null,
        });
        // Route to the new timesheet's detail page. Navigate with `replace: true` so it
        // supersedes the dialog's history entry; FullscreenFormDialog then sees its entry
        // is gone and skips its own history.back() on close (no race, no leftover entry).
        // Navigate first, then close.
        orgNavigate(`/timesheets/${newTimesheet.id}`, { replace: true });
        onOpenChange(false);
        form.reset();
      }
    } catch (error) {
      console.error('Failed to save timesheet:', error);
      // Mutation hooks already show error toasts
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset the form when the dialog closes; the open-effect re-initializes on next open.
  const handleOpenChange = (value: boolean) => {
    if (!value) form.reset();
    onOpenChange(value);
  };

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={isEditMode ? t('dialog.editTitle') : t('dialog.createTitle')}
      description={isEditMode ? t('dialog.editDescription') : t('dialog.createDescription')}
      submitLabel={isEditMode ? t('actions.saveChanges') : t('actions.createTimesheet')}
      submittingLabel={isEditMode ? t('actions.saving') : t('actions.creating')}
      isSubmitting={isSubmitting}
      skipCloseConfirmation={!form.formState.isDirty}
      onSubmit={form.handleSubmit(onSubmit)}
      formId="timesheet-form"
    >
      <Form {...form}>
        <div className="space-y-8">
          {/* Assignment links row — outside the card (mirrors the invoice's Project/Fund/AR row) */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {t('dialog.assignment')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="project_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">{t('columns.project')}</FormLabel>
                    <Select
                      value={field.value || 'none'}
                      onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('dialog.selectProject')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">{t('dialog.none')}</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="business_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">{t('columns.business')}</FormLabel>
                    <Select
                      value={field.value || 'none'}
                      onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('dialog.selectBusiness')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">{t('dialog.none')}</SelectItem>
                        {businesses.map((business) => (
                          <SelectItem key={business.id} value={business.id}>
                            {business.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="person_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs text-muted-foreground">{t('columns.person')}</FormLabel>
                    <Select
                      value={field.value || 'none'}
                      onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('dialog.selectPerson')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">{t('dialog.none')}</SelectItem>
                        {people.map((person) => (
                          <SelectItem key={person.id} value={person.id}>
                            {person.first_name} {person.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Timesheet content card — wraps the subject and the column configuration */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              {/* SECTION: details (hero title + fields) */}
              <div>
                <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                  {t('dialog.heroTitle')}
                </h2>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('dialog.details')}
                </h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel>{t('columns.name')}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{t('validation.nameRequired')}</span>
                          )}
                        </div>
                        <FormControl>
                          <Input
                            placeholder={t('dialog.namePlaceholder')}
                            className={fieldState.error ? "border-destructive" : ""}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('columns.notes')}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t('dialog.descriptionPlaceholder')}
                            className="resize-none"
                            rows={2}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="visibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('dialog.visibility')}</FormLabel>
                        <FormControl>
                          <VisibilitySelect
                            value={field.value as VisibilityLevel}
                            onChange={field.onChange}
                            allowedLevels={isAdmin ? undefined : NON_ADMIN_VISIBILITY_LEVELS}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Full-bleed colored section: visible columns. Uses theme-aware `bg-muted`
                  (not the invoice's frozen `bg-releve-gray-light`) because the checkbox
                  labels render theme-aware text that must stay legible in dark mode. */}
              <div className="-mx-6 px-6 py-6 bg-muted">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {t('dialog.visibleColumns')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Object.entries(columnLabelKeys).map(([key, labelKey]) => (
                    <FormField
                      key={key}
                      control={form.control}
                      name={`columns.${key as keyof FormValues['columns']}`}
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={key === 'entry_date' || key === 'start_time' || key === 'end_time'}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal cursor-pointer">
                            {t(labelKey)}
                            {(key === 'entry_date' || key === 'start_time' || key === 'end_time') && (
                              <span className="text-muted-foreground ml-1">{t('dialog.required')}</span>
                            )}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Form>
    </FullscreenFormDialog>
  );
}